import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';

// ── Lazy-load hls.js from CDN once ────────────────────────────────────────────
let _hlsPromise = null;
function loadHls() {
  if (_hlsPromise) return _hlsPromise;
  _hlsPromise = new Promise((resolve) => {
    if (window.Hls) return resolve(window.Hls);
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest/dist/hls.min.js';
    script.onload = () => resolve(window.Hls);
    document.head.appendChild(script);
  });
  return _hlsPromise;
}

// ── Mux HLS player ────────────────────────────────────────────────────────────
function MuxPlayer({ playbackId, muxStatus }) {
  const videoRef = useRef(null);
  const hlsRef   = useRef(null);
  const isLive   = muxStatus === 'active';

  useEffect(() => {
    // Only attempt HLS load when the stream is actually live
    if (!playbackId || !isLive || !videoRef.current) return;

    const src = `https://stream.mux.com/${playbackId}.m3u8`;

    loadHls().then(Hls => {
      // Tear down any previous instance before creating a new one
      hlsRef.current?.destroy();
      hlsRef.current = null;

      if (Hls.isSupported()) {
        const hls = new Hls({ lowLatencyMode: true });
        hlsRef.current = hls;
        hls.loadSource(src);
        hls.attachMedia(videoRef.current);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          videoRef.current?.play().catch(() => {});
        });
      } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari native HLS
        videoRef.current.src = src;
        videoRef.current.play().catch(() => {});
      }
    });

    return () => {
      hlsRef.current?.destroy();
      hlsRef.current = null;
    };
  }, [playbackId, isLive]); // re-runs when stream goes live

  if (!isLive) {
    return (
      <Box sx={{
        width: '100%', height: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#0f0f0f',
      }}>
        <Box sx={{ fontSize: '0.78rem', color: '#94a3b8', textAlign: 'center', userSelect: 'none' }}>
          {muxStatus === 'disabled' ? 'Stream ended' : 'The stream will start shortly'}
        </Box>
      </Box>
    );
  }

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted
      controls
      style={{ width: '100%', height: '100%', display: 'block', background: '#000' }}
    />
  );
}

// ── Iframe / YouTube / Mux stream embed ──────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export function EngStreamElement({ hotspot }) {
  const action     = hotspot.hotspotAction || {};
  const x          = hotspot.points?.[0]?.x ?? 10;
  const y          = hotspot.points?.[0]?.y ?? 10;
  const w          = hotspot.meta?.width    ?? 30;
  const h          = hotspot.meta?.height   ?? 16.875;

  const streamType = action.streamType || 'iframe';
  const hideUI     = !!action.hideUI;

  // ── Mux: own live status so stale DB values self-correct ──────────────────
  const [liveStatus,     setLiveStatus]     = useState(action.muxStatus     || null);
  const [livePlaybackId, setLivePlaybackId] = useState(action.muxPlaybackId || null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (streamType !== 'mux' || !action.muxStreamId) return;

    // Sync immediately from props when hotspot data updates
    setLiveStatus(action.muxStatus || null);
    setLivePlaybackId(action.muxPlaybackId || null);

    const poll = async () => {
      try {
        const res = await fetch(`${API_BASE}/mux/livestreams/${action.muxStreamId}`);
        if (!res.ok) return;
        const json = await res.json();
        if (json.success && json.data) {
          setLiveStatus(json.data.muxStatus     || null);
          setLivePlaybackId(json.data.muxPlaybackId || null);
        }
      } catch (_) { /* network blip — keep last known status */ }
    };

    // Poll immediately, then every 8 s while not yet live; slow to 30 s once live
    poll();
    pollRef.current = setInterval(() => {
      poll();
    }, liveStatus === 'active' ? 30000 : 8000);

    return () => clearInterval(pollRef.current);
  }, [action.muxStreamId]); // eslint-disable-line

  // Speed up polling when status flips to non-active
  useEffect(() => {
    if (streamType !== 'mux' || !action.muxStreamId) return;
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/mux/livestreams/${action.muxStreamId}`);
        if (!res.ok) return;
        const json = await res.json();
        if (json.success && json.data) {
          setLiveStatus(json.data.muxStatus     || null);
          setLivePlaybackId(json.data.muxPlaybackId || null);
        }
      } catch (_) {}
    }, liveStatus === 'active' ? 30000 : 8000);
    return () => clearInterval(pollRef.current);
  }, [liveStatus]); // eslint-disable-line

  // ── Non-Mux streams ───────────────────────────────────────────────────────
  let src = action.streamUrl || '';

  if (streamType === 'iframe' && src) {
    const m = src.match(/src=["']([^"']+)["']/i);
    if (m) src = m[1];
    src = src + (src.includes('?') ? '&' : '?') + 'autoplay=1';
  }

  if (streamType === 'youtube' && src) {
    const m  = src.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    const id = m?.[1] || (src.length === 11 ? src : null);
    if (id) {
      const params = new URLSearchParams({ autoplay: '1', mute: '1', rel: '0', ...(hideUI ? { controls: '0', modestbranding: '1' } : {}) });
      src = `https://www.youtube.com/embed/${id}?${params.toString()}`;
    } else { src = ''; }
  }

  // ── Mux — render HLS player with live-polled status ──
  if (streamType === 'mux') {
    return (
      <Box sx={{
        position: 'absolute',
        left: `${x}%`, top: `${y}%`,
        width: `${w}%`, height: `${h}%`,
        zIndex: 5 + (hotspot.order ?? 0),
        overflow: 'hidden',
        borderRadius: '4px',
        background: '#000',
        transition: 'left 0.45s cubic-bezier(0.4,0,0.2,1), top 0.45s cubic-bezier(0.4,0,0.2,1), width 0.45s cubic-bezier(0.4,0,0.2,1), height 0.45s cubic-bezier(0.4,0,0.2,1)',
      }}>
        <MuxPlayer playbackId={livePlaybackId} muxStatus={liveStatus} />
      </Box>
    );
  }

  if (!src) return null;

  return (
    <Box sx={{ position: 'absolute', left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%`, zIndex: 5 + (hotspot.order ?? 0), overflow: 'hidden', borderRadius: '4px', background: '#000', transition: 'left 0.45s cubic-bezier(0.4,0,0.2,1), top 0.45s cubic-bezier(0.4,0,0.2,1), width 0.45s cubic-bezier(0.4,0,0.2,1), height 0.45s cubic-bezier(0.4,0,0.2,1)' }}>
      <iframe src={src} style={{ width: '100%', height: '100%', border: 'none', display: 'block' }} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen title={hotspot.label || 'Livestream Player'} />
    </Box>
  );
}

// ── Full-screen intro video (plays once on first lobby load) ──────────────────
export function IntroOverlay({ videoUrl, onDone }) {
  const [phase, setPhase] = useState('playing');
  const videoRef = useRef(null);
  const handleEnded = () => { setPhase('fading'); setTimeout(onDone, 600); };

  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.play().catch(() => {
      onDone();
    });
  }, [videoUrl]); // eslint-disable-line

  return (
    <Box sx={{ position: 'absolute', inset: 0, zIndex: 9500, background: '#000', opacity: phase === 'fading' ? 0 : 1, transition: phase === 'fading' ? 'opacity 0.6s ease' : 'none', pointerEvents: phase === 'fading' ? 'none' : 'all' }}>
      <video
        ref={videoRef}
        src={videoUrl}
        autoPlay
        muted
        playsInline
        onEnded={handleEnded}
        onError={onDone}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </Box>
  );
}

// ── Transition overlay (fade-in, play video, fade-out between lobbies) ────────
export function TransitionOverlay({ videoUrl, onDone, onFadedIn }) {
  const [opacity,  setOpacity]  = useState(0);
  const [playing,  setPlaying]  = useState(false);
  const [fadeout,  setFadeout]  = useState(false);
  const fadedInFired = useRef(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setOpacity(1));
    const t   = setTimeout(() => {
      setPlaying(true);
      if (!fadedInFired.current) { fadedInFired.current = true; onFadedIn?.(); }
    }, 400);
    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
  }, []); // eslint-disable-line

  const handleEnded = () => { setFadeout(true); setOpacity(0); setTimeout(onDone, 600); };

  return (
    <Box sx={{ position: 'absolute', inset: 0, zIndex: 9999, background: '#000', opacity, transition: fadeout ? 'opacity 0.6s ease' : playing ? 'none' : 'opacity 0.4s ease', pointerEvents: fadeout ? 'none' : 'all' }}>
      <video src={videoUrl} autoPlay playsInline onEnded={handleEnded} onError={onDone} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
    </Box>
  );
}